import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

type PageState =
  | { status: 'validating' }
  | { status: 'creating' }
  | { status: 'redirecting' }
  | { status: 'success' }
  | { status: 'error'; message: string };

/**
 * CLI Auth Page — creates an API key and redirects to the CLI's localhost callback.
 *
 * URL: /developer/cli-auth?port=54321&state=abc123
 *
 * Flow:
 * 1. CLI opens browser to this page with port + state params
 * 2. ProtectedRoute ensures user is authenticated
 * 3. This page creates an API key via POST /v1/api-keys
 * 4. Redirects to http://localhost:{port}/callback?key={secret}&state={state}
 * 5. CLI receives the key and saves it
 */
export function CliAuthPage() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<PageState>({ status: 'validating' });

  const port = searchParams.get('port');
  const stateParam = searchParams.get('state');

  useEffect(() => {
    if (!port || !stateParam) {
      setState({
        status: 'error',
        message: 'Missing required parameters. This page should be opened by the FIAS CLI.',
      });
      return;
    }

    const portNum = parseInt(port, 10);
    if (isNaN(portNum) || portNum < 1024 || portNum > 65535) {
      setState({ status: 'error', message: 'Invalid port number.' });
      return;
    }

    let cancelled = false;

    async function authorize() {
      setState({ status: 'creating' });

      try {
        const result = await api.createApiKey({
          name: 'FIAS CLI (auto-generated)',
          scopes: ['developer'],
        });

        if (cancelled) return;

        setState({ status: 'redirecting' });

        // Redirect to the CLI's localhost callback with the API key
        const callbackUrl = new URL(`http://localhost:${port}/callback`);
        callbackUrl.searchParams.set('key', result.secret);
        callbackUrl.searchParams.set('state', stateParam!);

        window.location.href = callbackUrl.toString();
      } catch (err) {
        if (cancelled) return;
        setState({
          status: 'error',
          message: err instanceof Error ? err.message : 'Failed to create API key',
        });
      }
    }

    authorize();

    return () => {
      cancelled = true;
    };
  }, [port, stateParam]);

  function handleRetry() {
    setState({ status: 'validating' });
    // Re-trigger the effect by forcing a re-render won't work since deps haven't changed.
    // Instead, directly call the authorize flow again.
    const portNum = parseInt(port!, 10);
    if (isNaN(portNum) || portNum < 1024 || portNum > 65535 || !stateParam) return;

    setState({ status: 'creating' });
    api
      .createApiKey({ name: 'FIAS CLI (auto-generated)', scopes: ['developer'] })
      .then((result) => {
        setState({ status: 'redirecting' });
        const callbackUrl = new URL(`http://localhost:${portNum}/callback`);
        callbackUrl.searchParams.set('key', result.secret);
        callbackUrl.searchParams.set('state', stateParam);
        window.location.href = callbackUrl.toString();
      })
      .catch((err) => {
        setState({
          status: 'error',
          message: err instanceof Error ? err.message : 'Failed to create API key',
        });
      });
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '80px' }}>
      <Card style={{ maxWidth: '480px', width: '100%' }}>
        <CardHeader>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>FIAS CLI Authorization</h2>
        </CardHeader>
        <CardContent>
          {(state.status === 'validating' || state.status === 'creating') && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  border: '3px solid #e5e7eb',
                  borderTopColor: '#3b82f6',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                  margin: '0 auto 16px',
                }}
              />
              <p>Creating API key for FIAS CLI...</p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {state.status === 'redirecting' && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <p>Redirecting to CLI...</p>
            </div>
          )}

          {state.status === 'success' && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <p style={{ color: '#22c55e', fontWeight: 600, fontSize: '1.1rem' }}>
                Authentication successful!
              </p>
              <p style={{ marginTop: '8px', color: '#6b7280' }}>
                You can close this tab and return to your terminal.
              </p>
            </div>
          )}

          {state.status === 'error' && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <p style={{ color: '#ef4444', fontWeight: 600 }}>Authorization failed</p>
              <p style={{ marginTop: '8px', color: '#6b7280' }}>{state.message}</p>
              {port && stateParam && (
                <Button onClick={handleRetry} style={{ marginTop: '16px' }}>
                  Try again
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
